import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { UserService } from 'src/app/services/user.service';
import { API } from 'src/environments/environment';

interface ArvoreNode {
  id: string;
  tag: string;
  name: string;
  qtdNascimento: number;
  qtdCasamento: number;
  qtdOutro: number;
  qtdNN: number;
  qtdProcuracao: number;
  qtdRequerente: number;
  qtdMenor: number;
  obs: string;
  parent: string;
  ehCCNascimento: 'S' | 'N';
  ehCCCasamento: 'S' | 'N';
}

interface ResultadoParcela {
  etapa: string;
  parcela: number;
  tempo: number;
  totaGrupo: number;
  parcelaCadaMenor: number;
  parcelaCadaMaior: number;
  formaPagamento: string;
  totalDocumentos?: number;
  totalProcuracoes?: number;
  totalMaiores?: number;
  totalMenores?: number;
  cambioDia?: number;
  copiaConforme?: number;
}

@Component({
  selector: 'app-novo-orcamento-consulta',
  templateUrl: './novo-orcamento-consulta.component.html',
  styleUrls: ['./novo-orcamento-consulta.component.scss']
})
export class NovoOrcamentoConsultaComponent implements OnInit {
  idConsulta = '';
  idOrcamento = '';
  titulo = '';
  tribunal = '';
  observacao = '';

  loading = false;
  calculando = false;
  salvando = false;
  tree: ArvoreNode[] = [this.criarNoRaiz()];
  resultadoParcelas: ResultadoParcela[] = [];
  totalOrcamento = { totalGrupo: 0, totalCadaMenor: 0, totalCadaMaior: 0 };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ajaxService: AjaxService,
    private util: UtilService,
    private userService: UserService
  ) { }

  ngOnInit(): void {
    this.idConsulta =
      this.route.snapshot.queryParamMap.get('idConsulta')
      || this.route.snapshot.queryParamMap.get('id_consulta')
      || '';
    this.idOrcamento =
      this.route.snapshot.queryParamMap.get('idOrcamento')
      || this.route.snapshot.queryParamMap.get('id_orcamento')
      || '';

    if (!this.idConsulta) {
      this.util.toasterShowError('Atenção', 'Consulta inválida para criar orçamento.');
      return;
    }

    if (this.idOrcamento) {
      this.carregarOrcamentoExistente();
    }
  }

  private carregarOrcamentoExistente(): void {
    this.loading = true;
    this.util.showLoading();
    this.ajaxService
      .get(`${API}empresa/Consulta/ObterOrcamento?idOrcamento=${encodeURIComponent(this.idOrcamento)}`)
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.util.closeLoading();
          if (response?.hasErrors) {
            this.util.toasterShowError('Erro', response?.message || 'Não foi possível carregar o orçamento.');
            return;
          }

          const data = this.util.converterParaLowercase(response?.data || {});
          const orcamento = this.util.converterParaLowercase(data?.orcamento || {});
          const itens = this.normalizarLista(data?.orcamentoitens).map((x: any) => this.util.converterParaLowercase(x || {}));
          const preArvore = this.normalizarLista(data?.prearvore).map((x: any) => this.util.converterParaLowercase(x || {}));

          this.titulo = orcamento?.titulo || '';
          this.tribunal = orcamento?.tribunal || '';
          this.observacao = orcamento?.observacaogeral || '';

          if (preArvore.length > 0) {
            this.tree = preArvore.map((x: any) => ({
              id: x.guid || this.generateUUID(),
              tag: x.tag || '',
              name: x.nome || '',
              qtdNascimento: Number(x.nascimento || 0),
              qtdCasamento: Number(x.casamento || 0),
              qtdOutro: Number(x.outros || 0),
              qtdNN: Number(x.negativanaturalizacao || 0),
              qtdProcuracao: Number(x.procuracao || 0),
              qtdRequerente: Number(x.requerentes || 0),
              qtdMenor: Number(x.menores || 0),
              obs: x.observacao || '',
              parent: x.parent || '',
              ehCCNascimento: x.ehccnascimento === 'S' ? 'S' : 'N',
              ehCCCasamento: x.ehcccasamento === 'S' ? 'S' : 'N'
            } as ArvoreNode));
          }

          this.resultadoParcelas = itens.map((x: any) => ({
            etapa: x.etapa || '',
            parcela: Number(x.parcela || 0),
            tempo: Number(x.tempo || 0),
            totaGrupo: Number(x.totagrupo || 0),
            parcelaCadaMenor: Number(x.parcelacadamenor || 0),
            parcelaCadaMaior: Number(x.parcelacadamaior || 0),
            formaPagamento: x.formapagamento || ''
          }));
          this.funcTotalOrcamento();
        },
        error: () => {
          this.loading = false;
          this.util.closeLoading();
          this.util.toasterShowError('Erro', 'Erro ao carregar orçamento.');
        }
      });
  }

  private criarNoRaiz(): ArvoreNode {
    return {
      id: this.generateUUID(),
      tag: '1',
      name: '',
      qtdNascimento: 0,
      qtdCasamento: 0,
      qtdOutro: 0,
      qtdNN: 0,
      qtdProcuracao: 0,
      qtdRequerente: 0,
      qtdMenor: 0,
      obs: '',
      parent: '',
      ehCCNascimento: 'N',
      ehCCCasamento: 'N'
    };
  }

  addNewLine(parent: ArvoreNode, index: number): void {
    const novoNo: ArvoreNode = {
      id: this.generateUUID(),
      tag: '',
      name: '',
      qtdNascimento: 0,
      qtdCasamento: 0,
      qtdOutro: 0,
      qtdNN: 0,
      qtdProcuracao: 0,
      qtdRequerente: 0,
      qtdMenor: 0,
      obs: '',
      parent: parent.id,
      ehCCNascimento: 'N',
      ehCCCasamento: 'N'
    };

    this.tree.splice(index + 1, 0, novoNo);
    this.enumerateNodes();
  }

  removeNo(node: ArvoreNode): void {
    if (!confirm('Você tem certeza que deseja deletar?\nAo confirmar irá deletar todos os nós relacionados!')) {
      return;
    }

    this.removeNodeAndChildren(node.id);
    if (this.tree.length === 0) {
      this.tree = [this.criarNoRaiz()];
    } else {
      this.enumerateNodes();
    }
  }

  private removeNodeAndChildren(id: string): void {
    const remover = new Set<string>();
    const coletar = (nodeId: string) => {
      remover.add(nodeId);
      this.tree.filter(x => x.parent === nodeId).forEach(filho => coletar(filho.id));
    };

    coletar(id);
    this.tree = this.tree.filter(x => !remover.has(x.id));
  }

  private enumerateNodes(): void {
    const enumerar = (parentId: string, prefixo: string) => {
      let contador = 1;
      this.tree.forEach(node => {
        if (node.parent === parentId) {
          node.tag = `${prefixo}${contador}`;
          enumerar(node.id, node.tag);
          contador += 1;
        }
      });
    };

    enumerar('', '');
  }

  private verificarVazioENegativo(): void {
    const normalizar = (valor: any): number => {
      const numero = Number(valor || 0);
      if (Number.isNaN(numero) || numero < 0) return 0;
      return numero;
    };

    this.tree.forEach(node => {
      node.qtdNascimento = normalizar(node.qtdNascimento);
      node.qtdCasamento = normalizar(node.qtdCasamento);
      node.qtdOutro = normalizar(node.qtdOutro);
      node.qtdNN = normalizar(node.qtdNN);
      node.qtdProcuracao = normalizar(node.qtdProcuracao);
      node.qtdRequerente = normalizar(node.qtdRequerente);
      node.qtdMenor = normalizar(node.qtdMenor);
      node.ehCCNascimento = node.ehCCNascimento === 'S' ? 'S' : 'N';
      node.ehCCCasamento = node.ehCCCasamento === 'S' ? 'S' : 'N';
    });
  }

  private montarParcelasCalculo(): any[] {
    return this.tree.map((node, index) => ({
      Geracao: node.tag,
      Nome: node.name || '',
      Nascimento: Number(node.qtdNascimento || 0),
      Casamento: Number(node.qtdCasamento || 0),
      Outros: Number(node.qtdOutro || 0),
      NegativaNaturalizacao: Number(node.qtdNN || 0),
      Procuracao: Number(node.qtdProcuracao || 0),
      Requerentes: Number(node.qtdRequerente || 0),
      Menores: Number(node.qtdMenor || 0),
      Observacoes: node.obs || '',
      ehCCNascimento: node.ehCCNascimento || 'N',
      ehCCCasamento: node.ehCCCasamento || 'N',
      ehItaliano: index === 0
    }));
  }

  realizarOrcamento(): void {
    this.verificarVazioENegativo();
    const payload = {
      ORCAMENTO_DOCUMENTOS: this.montarParcelasCalculo()
    };

    this.calculando = true;
    this.util.showLoading();
    this.ajaxService.post(`${API}empresa/Consulta/OrcamentoAutomatizadoCalcular`, payload).subscribe({
      next: (response) => {
        this.calculando = false;
        this.util.closeLoading();

        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response?.message || 'Não foi possível calcular o orçamento.');
          return;
        }

        const lista = this.normalizarLista(response?.data);
        this.resultadoParcelas = lista.map((x: any) => this.util.converterParaLowercase(x || {})) as ResultadoParcela[];
        this.funcTotalOrcamento();
      },
      error: () => {
        this.calculando = false;
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Erro ao calcular orçamento.');
      }
    });
  }

  limparTableResultado(): void {
    this.resultadoParcelas = [];
    this.totalOrcamento = { totalGrupo: 0, totalCadaMenor: 0, totalCadaMaior: 0 };
  }

  private funcTotalOrcamento(): void {
    this.totalOrcamento = { totalGrupo: 0, totalCadaMenor: 0, totalCadaMaior: 0 };
    this.resultadoParcelas.forEach((item: any) => {
      this.totalOrcamento.totalGrupo += Number(item.totagrupo || item.totaGrupo || 0);
      this.totalOrcamento.totalCadaMaior += Number(item.parcelacadamaior || item.parcelaCadaMaior || 0);
      this.totalOrcamento.totalCadaMenor += Number(item.parcelacadamenor || item.parcelaCadaMenor || 0);
    });
  }

  private montarPreArvore(): any[] {
    return this.tree.map((node) => ({
      NOME: node.name || '',
      NASCIMENTO: Number(node.qtdNascimento || 0),
      CASAMENTO: Number(node.qtdCasamento || 0),
      OUTROS: Number(node.qtdOutro || 0),
      NEGATIVA_NATURALIZACAO: Number(node.qtdNN || 0),
      PROCURACAO: Number(node.qtdProcuracao || 0),
      REQUERENTES: Number(node.qtdRequerente || 0),
      MENORES: Number(node.qtdMenor || 0),
      OBSERVACAO: node.obs || '',
      BUSCAR_DOC_ITALIANOS: false,
      BUSCAR_DOC_BRASILEIROS: false,
      TAG: node.tag || '',
      GUID: node.id,
      PARENT: node.parent || '',
      ehCCNascimento: node.ehCCNascimento || 'N',
      ehCCCasamento: node.ehCCCasamento || 'N'
    }));
  }

  private montarOrcamentoItens(): any[] {
    return this.resultadoParcelas.map((item: any) => ({
      ETAPA: item.etapa || '',
      PARCELA: Number(item.parcela || 0),
      TEMPO: Number(item.tempo || 0),
      TOTAL_GRUPO: Number(item.totagrupo || item.totaGrupo || 0),
      PARCELA_CADA_MENOR: Number(item.parcelacadamenor || item.parcelaCadaMenor || 0),
      PARCELA_CADA_MAIOR: Number(item.parcelacadamaior || item.parcelaCadaMaior || 0),
      FORMA_PAGAMENTO: item.formapagamento || item.formaPagamento || ''
    }));
  }

  formatarNumero(valor: number): string {
    const numero = Number(valor || 0);
    return numero.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  salvar(): void {
    if (!this.titulo?.trim()) {
      this.util.toasterShowError('Atenção', 'Informe o título do orçamento.');
      return;
    }

    if (this.resultadoParcelas.length === 0) {
      this.util.toasterShowError('Atenção', 'Realize o orçamento antes de salvar.');
      return;
    }

    const idUsuarioLogado = this.getIdUsuarioLogado();
    if (!idUsuarioLogado) {
      this.util.toasterShowError('Atenção', 'Usuário logado inválido para salvar orçamento.');
      return;
    }

    const payload = {
      idConsulta: this.idConsulta,
      idUsuario: idUsuarioLogado,
      titulo: this.titulo.trim(),
      tribunal: this.tribunal || '',
      observacaoGeral: this.observacao || '',
      observacaoImpressao: '',
      desconto: 0,
      acrescimo: 0,
      preArvore: this.montarPreArvore(),
      orcamentoItens: this.montarOrcamentoItens()
    };

    this.salvando = true;
    this.util.showLoading();
    this.ajaxService.post(`${API}empresa/Consulta/SalvarOrcamentoArvore`, payload).subscribe({
      next: (response) => {
        this.salvando = false;
        this.util.closeLoading();
        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response?.message || 'Não foi possível salvar o orçamento.');
          return;
        }
        this.util.toasterShowSuccess('Sucesso', 'Orçamento salvo com sucesso.');
        this.router.navigate(['/orcamentos-consulta'], {
          queryParams: { idConsulta: this.idConsulta }
        });
      },
      error: () => {
        this.salvando = false;
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Erro ao salvar orçamento.');
      }
    });
  }

  private getIdUsuarioLogado(): number | null {
    const userData = this.userService.getUserData();
    if (!userData) return null;

    const userId =
      Number(userData.User?.Id) ||
      Number((userData as any)?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/sid']) ||
      0;

    return userId > 0 ? userId : null;
  }

  private normalizarLista(data: any): any[] {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'object') return Object.values(data);
    return [];
  }

  private generateUUID(): string {
    let d = new Date().getTime();
    let d2 = (performance && performance.now && (performance.now() * 1000)) || 0;
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      let r = Math.random() * 16;
      if (d > 0) {
        r = (d + r) % 16 | 0;
        d = Math.floor(d / 16);
      } else {
        r = (d2 + r) % 16 | 0;
        d2 = Math.floor(d2 / 16);
      }
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }

  voltar(): void {
    this.router.navigate(['/orcamentos-consulta'], {
      queryParams: { idConsulta: this.idConsulta }
    });
  }
}
